/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, useEffect, useState, useCallback} from 'react'
import {Dropdown, FormControl, Spinner} from 'react-bootstrap'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {getAllHSDescriptionParent, getHSDescriptionByID} from '../core/_requests'
import lodash from 'lodash'

type ParentDropdownProps = {
  onSelect: (econ: any) => void
  disabled?: boolean
  parent?: string
}

type selectedParentType = {
  id: string | number
  version: string
  description: string
}

const ParentDropdown: FC<ParentDropdownProps> = ({onSelect, disabled = false, parent}) => {
  const axiosPrivate = useAxiosPrivate()
  const [parents, setParents] = useState<Array<any>>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedParent, setSelectedParent] = useState<selectedParentType>()
  const [offset, setOffset] = useState<number>(0)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  const limit = 50

  const fetchData = async (search: string, currentOffset: number, isAppending: boolean = false) => {
    try {
      setLoading(true)
      const res = await getAllHSDescriptionParent(axiosPrivate, {
        limit,
        offset: currentOffset,
        search,
      })

      const newParents = res.data.data

      if (newParents.length < limit) {
        setHasMore(false)
      } else {
        setHasMore(true)
      }

      if (isAppending) {
        setParents((prev) => {
          // Filter out duplicates just in case
          const existingIds = new Set(prev.map((p) => p.id))
          const uniqueNewParents = newParents.filter((p: any) => !existingIds.has(p.id))
          return [...prev, ...uniqueNewParents]
        })
      } else {
        setParents(newParents)
      }
    } catch (e) {
      console.error('Failed to fetch parents', e)
    } finally {
      setLoading(false)
    }
  }

  const debouncedSearch = useCallback(
    lodash.debounce((search: string) => {
      setOffset(0)
      fetchData(search, 0, false)
    }, 500),
    []
  )

  useEffect(() => {
    fetchData('', 0, false)
  }, [])

  const normalizeId = (id: any) => {
    if (id === undefined || id === null || id === '') return ''
    const filtered = String(id).replaceAll('0', '')
    return filtered === '' ? '0' : filtered
  }

  useEffect(() => {
    if (!parent) {
      setSelectedParent(undefined)
      return
    }

    const normalizedParent = normalizeId(parent)
    const found = parents.find((e) => normalizeId(e.id) === normalizedParent)

    if (found) {
      setSelectedParent(found)
    } else if (!selectedParent || normalizeId(selectedParent.id) !== normalizedParent) {
      getHSDescriptionByID(axiosPrivate, {idHS: parent})
        .then((res) => {
          if (res.data.data && res.data.data.original) {
            setSelectedParent({
              id: res.data.data.original.id,
              version: res.data.data.original.version,
              description: res.data.data.original.description,
            })
          }
        })
        .catch((e) => {
          console.error('Failed to fetch selected parent by ID', e)
        })
    }
  }, [parent, parents])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    debouncedSearch(value)
  }

  const handleLoadMore = (e: React.MouseEvent) => {
    e.stopPropagation()
    const nextOffset = offset + limit
    setOffset(nextOffset)
    fetchData(searchTerm, nextOffset, true)
  }

  const handleSelect = (parentItem: any) => {
    console.log(parentItem)
    onSelect(normalizeId(parentItem.id))
    setSelectedParent(parentItem)
  }

  const handleToggle = (isOpen: boolean) => {
    setIsDropdownOpen(isOpen)
    if (!isOpen && searchTerm !== '') {
      setSearchTerm('')
      setOffset(0)
      fetchData('', 0, false)
    }
  }

  const dropdownItem = parents.map((parentItem) => {
    return (
      <Dropdown.Item
        key={`${Math.random()}_${parentItem.id}-${parentItem.version}`}
        role='menuitem'
        size='sm'
        onClick={() => handleSelect(parentItem)}
        title={`(${parentItem.version}) ${parentItem.description}`}
        style={{whiteSpace: 'normal'}}
      >
        ({parentItem.version}) {parentItem.description}
      </Dropdown.Item>
    )
  })

  return (
    <Dropdown onToggle={handleToggle} show={isDropdownOpen}>
      <Dropdown.Toggle
        variant='label-dropdown'
        id=''
        className='btn btn-sm btn-bold dropdown-toggle fw-normal text-start w-100'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '44px',
          height: 'auto',
          whiteSpace: 'normal',
        }}
        disabled={disabled}
      >
        {selectedParent ? (
          <div
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            ({selectedParent.version}) {selectedParent.description}
          </div>
        ) : (
          'Select Parent'
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu
        className='dropdown-econ-menu w-100'
        style={{maxHeight: '300px', overflowY: 'auto'}}
      >
        <div
          style={{
            padding: '4px 8px',
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 1,
          }}
        >
          <FormControl
            type='text'
            size='sm'
            placeholder='Search parent...'
            className='dropdown-econ-search-bar'
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        {loading && offset === 0 ? (
          <div className='d-flex justify-content-center p-3'>
            <Spinner animation='border' size='sm' variant='primary' />
          </div>
        ) : (
          <>
            {parents.length === 0 && !loading && (
              <div className='text-muted text-center p-2' style={{fontSize: '0.85rem'}}>
                No results found in database.
              </div>
            )}
            {dropdownItem}
            {hasMore && parents.length > 0 && (
              <div className='text-center p-2'>
                <button
                  className='btn btn-sm btn-primary w-100'
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? <Spinner animation='border' size='sm' /> : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default ParentDropdown
